import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useInnerCircle } from '../context/InnerCircleContext';
import axios from 'axios';
import { Home, BookOpen, Book, User, LogOut, Layers, ShieldCheck, Info, Users, Menu, X } from 'lucide-react';
import { authUrlWithNext } from '../lib/whatsapp';
import MusicToggle from './MusicToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPendingRow = ({ post, onActioned, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [feedback, setFeedback] = useState('');
  const navigate = useNavigate();

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const publish = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/blog/${post.id}/publish`, {}, auth());
      onActioned();
    } catch (e) { /* ignore */ }
    finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/blog/${post.id}/reject`, { feedback }, auth());
      onActioned();
    } catch (e) { /* ignore */ }
    finally { setBusy(false); }
  };
  return (
    <div className="p-3 border-b border-primary/10 last:border-0" data-testid={`admin-pending-row-${post.id}`}>
      <div className="text-sm text-primary font-medium line-clamp-1">{post.title}</div>
      <div className="text-[11px] text-primary/60 mb-2">
        {post.author_name} · {post.updated_at ? new Date(post.updated_at).toLocaleDateString() : ''}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={publish}
          disabled={busy}
          className="px-2.5 py-1 text-[11px] border border-emerald-400/40 rounded text-emerald-200 hover:bg-emerald-500/10 transition"
          data-testid={`admin-pending-publish-${post.id}`}
        >
          Publish
        </button>
        <button
          type="button"
          onClick={() => setShowReject((v) => !v)}
          className="px-2.5 py-1 text-[11px] border border-rose-400/40 rounded text-rose-200 hover:bg-rose-500/10 transition"
          data-testid={`admin-pending-reject-${post.id}`}
        >
          Reject
        </button>
        <button
          type="button"
          onClick={() => { onClose(); navigate(`/blog/edit/${post.slug}`); }}
          className="px-2.5 py-1 text-[11px] border border-primary/30 rounded text-primary/90 hover:bg-primary/10 transition"
          data-testid={`admin-pending-edit-${post.id}`}
        >
          Edit
        </button>
      </div>
      {showReject && (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Short feedback (optional)"
            className="flex-1 bg-primary/5 border border-primary/20 rounded px-2 py-1 text-xs text-primary focus:outline-none focus:border-primary/60"
            data-testid={`admin-pending-feedback-${post.id}`}
          />
          <button
            type="button"
            onClick={reject}
            disabled={busy}
            className="px-2.5 py-1 text-[11px] bg-rose-500/20 border border-rose-400/40 rounded text-rose-100 hover:bg-rose-500/30 transition"
            data-testid={`admin-pending-reject-submit-${post.id}`}
          >
            Confirm reject
          </button>
        </div>
      )}
    </div>
  );
};

const Navigation = () => {
  const { user, logout } = useAuth();
  const { openIC } = useInnerCircle();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoTooltip, setShowLogoTooltip] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) { setPending([]); return; }
    const token = localStorage.getItem('token');
    const fetchPending = () => {
      axios.get(`${API}/blog/pending`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setPending(r.data || []))
        .catch(() => setPending([]));
    };
    fetchPending();
    const iv = setInterval(fetchPending, 60000);
    return () => clearInterval(iv);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/', label: 'Home', icon: Home, show: true },
    { to: '/dashboard', label: 'Dashboard', icon: User, show: !!user },
    { to: '/courses/1', label: 'Courses', icon: Layers, show: true },
    { to: '/about', label: 'About', icon: Info, show: true },
    { to: '/guides', label: 'Guides', icon: Users, show: true },
    { to: '/blog', label: 'Blogs', icon: BookOpen, show: true },
    { to: '/books', label: 'Books', icon: Book, show: true },
    { to: '/admin', label: 'Admin', icon: ShieldCheck, show: !!user?.is_admin },
  ];

  return (
    <nav className="fixed left-0 right-0 z-50 border-b border-primary/20 bg-background/80 backdrop-blur-md" style={{ top: 32 }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <div 
              className="relative"
              onMouseEnter={() => setShowLogoTooltip(true)}
              onMouseLeave={() => setShowLogoTooltip(false)}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_ebb063e1-3e83-418f-bb38-602ef1351305/artifacts/yspj6tyg_Union%20of%20Shiva%20and%20Parvati.png"
                alt="Siddha Samaj"
                width="28"
                height="28"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  borderRadius: '50%',
                  border: '1.5px solid rgba(212,168,67,0.6)',
                  objectFit: 'cover',
                  objectPosition: 'center 30%',
                  backgroundColor: '#0a0a0a',
                  boxShadow: '0 0 10px rgba(212,168,67,0.25)',
                }}
                data-testid="navbar-logo-img"
              />
              {showLogoTooltip && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-1 bg-card border border-primary/30 rounded-lg text-xs text-primary whitespace-nowrap">
                  A Siddha Samaj Initiative
                </div>
              )}
            </div>
            <div className="text-2xl font-heading golden-text font-bold tracking-wider">MyShaktiverse</div>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`flex items-center gap-2 text-sm font-heading uppercase tracking-wider transition-colors hover:text-primary ${
                isActive('/') ? 'text-primary' : 'text-primary/60'
              }`}
              data-testid="nav-home-link"
            >
              <Home size={18} />
              Home
            </Link>

            {user && (
              <Link
                to="/dashboard"
                className={`flex items-center gap-2 text-sm font-heading uppercase tracking-wider transition-colors hover:text-primary ${
                  isActive('/dashboard') ? 'text-primary' : 'text-primary/60'
                }`}
                data-testid="nav-dashboard-link"
              >
                <User size={18} />
                Dashboard
              </Link>
            )}

            <Link
              to="/courses/1"
              className={`flex items-center gap-2 text-sm font-heading uppercase tracking-wider transition-colors hover:text-primary ${
                location.pathname.startsWith('/courses') ? 'text-primary' : 'text-primary/60'
              }`}
              data-testid="nav-courses-link"
            >
              <Layers size={18} />
              Courses
            </Link>

            <Link
              to="/about"
              className={`flex items-center gap-2 text-sm font-heading uppercase tracking-wider transition-colors hover:text-primary ${
                isActive('/about') ? 'text-primary' : 'text-primary/60'
              }`}
              data-testid="nav-about-link"
            >
              <Info size={18} />
              About
            </Link>

            <Link
              to="/guides"
              className={`flex items-center gap-2 text-sm font-heading uppercase tracking-wider transition-colors hover:text-primary ${
                isActive('/guides') ? 'text-primary' : 'text-primary/60'
              }`}
              data-testid="nav-guides-link"
            >
              <Users size={18} />
              Guides
            </Link>

            <Link
              to="/blog"
              className={`flex items-center gap-2 text-sm font-heading uppercase tracking-wider transition-colors hover:text-primary ${
                isActive('/blog') ? 'text-primary' : 'text-primary/60'
              }`}
              data-testid="nav-blog-link"
            >
              <BookOpen size={18} />
              Blogs
            </Link>

            <Link
              to="/books"
              className={`flex items-center gap-2 text-sm font-heading uppercase tracking-wider transition-colors hover:text-primary ${
                isActive('/books') ? 'text-primary' : 'text-primary/60'
              }`}
              data-testid="nav-books-link"
            >
              <Book size={18} />
              Books
            </Link>

            {/* Inner Circle pill — small gold outlined pill after Books.
                Members see "Inner Circle" linking to /account/membership;
                non-members see "Join the Inner Circle" opening the modal. */}
            {user?.is_member ? (
              <Link
                to="/account/membership"
                className="hidden xl:inline-flex items-center text-[11px] font-heading uppercase tracking-[0.25em] rounded-full border border-primary/55 text-primary px-3 py-1 hover:bg-primary/10"
                data-testid="nav-inner-circle-manage"
              >
                Inner Circle
              </Link>
            ) : (
              <Link
                to="/inner-circle"
                className="hidden xl:inline-flex items-center text-[11px] font-heading uppercase tracking-[0.25em] rounded-full border border-primary/55 text-primary px-3 py-1 hover:bg-primary/10"
                data-testid="nav-inner-circle-join"
              >
                Join the Inner Circle
              </Link>
            )}

            {user?.is_admin && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setAdminDropdownOpen((v) => !v)}
                  className={`flex items-center gap-2 text-sm font-heading uppercase tracking-wider transition-colors hover:text-primary ${
                    isActive('/admin') ? 'text-primary' : 'text-primary/60'
                  }`}
                  data-testid="nav-admin-link"
                >
                  <ShieldCheck size={18} />
                  Admin
                  {pending.length > 0 && (
                    <span
                      className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-rose-500 text-white"
                      data-testid="admin-pending-badge"
                    >
                      {pending.length}
                    </span>
                  )}
                </button>
                {adminDropdownOpen && (
                  <div
                    className="absolute right-0 mt-3 w-[380px] card-mystical rounded-xl border border-primary/20 shadow-xl z-50"
                    data-testid="admin-pending-dropdown"
                  >
                    <div className="p-3 border-b border-primary/15 flex items-center justify-between">
                      <span className="text-xs uppercase tracking-[0.3em] text-primary/70">Pending Reviews</span>
                      <Link
                        to="/admin"
                        onClick={() => setAdminDropdownOpen(false)}
                        className="text-xs text-primary/70 hover:text-primary"
                      >
                        Open Admin →
                      </Link>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {pending.length === 0 ? (
                        <div className="p-5 text-sm text-primary/60 text-center">No posts awaiting review.</div>
                      ) : pending.map((p) => (
                        <AdminPendingRow
                          key={p.id}
                          post={p}
                          onActioned={() => setPending((list) => list.filter((x) => x.id !== p.id))}
                          onClose={() => setAdminDropdownOpen(false)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 md:gap-4">
            <MusicToggle />
            {user ? (
              <>
                <span className="hidden sm:inline text-sm text-primary/70 font-heading">
                  {user.name}
                </span>
                <button
                  onClick={handleLogout}
                  className="hidden sm:inline-flex ornate-button px-4 py-2 text-sm font-heading text-primary uppercase tracking-wider"
                  data-testid="logout-button"
                >
                  <LogOut size={16} className="mr-2 inline" />
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate(authUrlWithNext(location))}
                className="hidden sm:inline-flex ornate-button px-6 py-2 text-sm font-heading text-primary uppercase tracking-wider"
                data-testid="login-button"
              >
                Enter
              </button>
            )}

            {/* Mobile menu toggle */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden p-2 text-primary hover:bg-primary/10 rounded-lg transition"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="md:hidden border-t border-primary/20 bg-background/95 backdrop-blur-md"
          data-testid="mobile-menu-drawer"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.filter((l) => l.show).map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-heading uppercase tracking-wider transition-colors ${
                  isActive(to)
                    ? 'bg-primary/10 text-primary'
                    : 'text-primary/70 hover:bg-primary/5 hover:text-primary'
                }`}
                data-testid={`mobile-nav-${label.toLowerCase()}-link`}
              >
                <Icon size={18} />
                {label}
              </Link>
            ))}

            {/* Inner Circle entry (mobile) */}
            {user?.is_member ? (
              <Link
                to="/account/membership"
                onClick={() => setMobileOpen(false)}
                className="mt-1 inline-flex items-center justify-center text-[11px] font-heading uppercase tracking-[0.25em] rounded-full border border-primary/55 text-primary px-3 py-2"
                data-testid="mobile-inner-circle-manage"
              >
                Inner Circle
              </Link>
            ) : (
              <Link
                to="/inner-circle"
                onClick={() => setMobileOpen(false)}
                className="mt-1 inline-flex items-center justify-center text-[11px] font-heading uppercase tracking-[0.25em] rounded-full border border-primary/55 text-primary px-3 py-2"
                data-testid="mobile-inner-circle-join"
              >
                Join the Inner Circle
              </Link>
            )}

            <div className="h-px bg-primary/20 my-2" />

            {user ? (
              <>
                <div className="px-3 py-2 text-xs text-primary/60 font-heading uppercase tracking-wider">
                  Signed in as <span className="text-primary">{user.name}</span>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-heading uppercase tracking-wider text-primary/80 hover:bg-primary/10 transition-colors"
                  data-testid="mobile-logout-button"
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  navigate(authUrlWithNext(location));
                }}
                className="ornate-button mt-1 w-full py-3 text-sm font-heading text-primary uppercase tracking-wider"
                data-testid="mobile-login-button"
              >
                Enter
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
